import { useEffect } from 'react';
import { Info, X } from 'lucide-react';
import { useGuidanceDismissed } from '@/hooks/useGuidanceDismissed';
import { getGuidanceTip } from '@/data/userTips';
import styles from './GuidanceBanner.module.css';
import type { GuidanceBannerProps } from './GuidanceBanner.types';

/**
 * GuidanceBanner - Shows helpful tips on first visit to each screen.
 *
 * Dismiss behavior:
 * - X button alone: hides for current session only (re-appears on next visit)
 * - Checkbox "Don't show again" + X: permanently hides (persisted in localStorage)
 * - autoDismissMs prop: auto-dismiss after N ms (session only)
 */
export function GuidanceBanner({ screenId, className, autoDismissMs }: GuidanceBannerProps) {
  const tip = getGuidanceTip(screenId);
  const { isDismissed, dismiss, dismissForSession, dontShowAgain, setDontShowAgain } =
    useGuidanceDismissed(tip.id);

  // Auto-dismiss after a timeout (session only)
  useEffect(() => {
    if (!autoDismissMs || isDismissed) return;
    const timer = setTimeout(dismissForSession, autoDismissMs);
    return () => clearTimeout(timer);
  }, [autoDismissMs, isDismissed, dismissForSession]);

  if (isDismissed) {
    return null;
  }

  const classNames = [styles.banner, className].filter(Boolean).join(' ');

  return (
    <div className={classNames} role="region" aria-label="Guidance tip">
      <div className={styles.icon} aria-hidden="true">
        <Info size={18} />
      </div>

      <div className={styles.content}>
        <p className={styles.title}>{tip.title}</p>
        <p className={styles.description}>{tip.description}</p>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className={styles.checkbox}
          />
          <span>Don&apos;t show again</span>
        </label>
      </div>

      <button
        type="button"
        className={styles.dismissButton}
        onClick={dismiss}
        aria-label="Dismiss tip"
      >
        <X size={18} aria-hidden="true" />
      </button>
    </div>
  );
}
